import type { SourceType } from './recipe.ts';

/** Hosts per video platform (Supadata handles all of them, PLAN.md §5). */
const VIDEO_HOSTS: [RegExp, SourceType][] = [
  [/(^|\.)(youtube\.com|youtu\.be|youtube-nocookie\.com)$/, 'youtube'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)(x\.com|twitter\.com)$/, 'x'],
  [/(^|\.)(facebook\.com|fb\.watch|fb\.com)$/, 'facebook'],
];

export const isVideoSource = (type: SourceType) =>
  type === 'youtube' ||
  type === 'instagram' ||
  type === 'tiktok' ||
  type === 'x' ||
  type === 'facebook';

/** Parses user input as an http(s) URL, tolerating a missing scheme ("chefkoch.de/…"). */
export function parseUrl(input: string): URL | null {
  const text = input.trim();
  if (!text || /\s/.test(text)) return null;
  const candidate = /^https?:\/\//i.test(text)
    ? text
    : /^[\w-]+(\.[\w-]+)+(\/|$)/.test(text)
      ? `https://${text}`
      : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/** The first URL inside shared text ("Look at this! https://youtu.be/… #food"). */
export function findUrl(text: string): string | null {
  return /https?:\/\/[^\s<>"']+/i.exec(text)?.[0]?.replace(/[).,!?]+$/, '') ?? null;
}

/** Source type of an import input: a video platform, a website, or plain text. */
export function detectSource(input: string): SourceType {
  const url = parseUrl(input);
  if (!url) return 'text';
  const host = url.hostname.toLowerCase();
  for (const [pattern, type] of VIDEO_HOSTS) if (pattern.test(host)) return type;
  return 'web';
}

const TRACKING =
  /^(utm_\w+|fbclid|gclid|dclid|mc_\w+|igshid|igsh|si|feature|ref|ref_src|ref_url|s|t|share_id|_r|__twitter_impression|is_from_webapp|sender_device|web_id)$/i;

/**
 * A stable key for "the same source" (dedupe, PLAN.md §5 step 1): video ids for the platforms
 * (`youtube:dQw4w9WgXcQ`), else host + path + meaningful query without tracking parameters.
 */
export function normalizeUrl(input: string): string | null {
  const url = parseUrl(input);
  if (!url) return null;
  const host = url.hostname.toLowerCase().replace(/^(www|m|mobile)\./, '');
  const path = url.pathname.replace(/\/+$/, '');

  if (host === 'youtu.be') return `youtube:${path.slice(1)}`;
  if (/youtube(-nocookie)?\.com$/.test(host)) {
    const v = url.searchParams.get('v');
    if (v) return `youtube:${v}`;
    const id = /^\/(shorts|embed|live|v)\/([\w-]+)/.exec(path)?.[2];
    if (id) return `youtube:${id}`;
  }
  if (host === 'instagram.com') {
    const id = /^\/(?:[\w.]+\/)?(p|reel|reels|tv)\/([\w-]+)/.exec(path)?.[2];
    if (id) return `instagram:${id}`;
  }
  if (host === 'tiktok.com') {
    const id = /\/video\/(\d+)/.exec(path)?.[1];
    if (id) return `tiktok:${id}`;
  }
  if (host === 'x.com' || host === 'twitter.com') {
    const id = /\/status\/(\d+)/.exec(path)?.[1];
    if (id) return `x:${id}`;
  }

  const params = [...url.searchParams.entries()]
    .filter(([key]) => !TRACKING.test(key))
    .sort(([a], [b]) => a.localeCompare(b));
  const query = params.length > 0 ? `?${new URLSearchParams(params).toString()}` : '';
  return `${host}${path}${query}`;
}
